import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { invites } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";
import type { InvitePreview } from "@jotty/api-client";

type InviteInfo = InvitePreview;

// ---------------------------------------------------------------------------
// Password validation — mirrors auth.MinPasswordLength (12) in password.go
// ---------------------------------------------------------------------------

function validatePassword(pw: string): string | null {
  if (pw.length < 12) return "Password must be at least 12 characters.";
  if (pw.trim().length === 0) return "Password cannot be only whitespace.";
  return null;
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/invite/$token")({
  component: InviteSignUpPage,
});

// ---------------------------------------------------------------------------
// Page states
// ---------------------------------------------------------------------------

type PageState =
  | "loading"
  | "ready"
  | "error_not_found"
  | "error_expired"
  | "error_consumed"
  | "error_generic";

const ERROR_COPY: Partial<
  Record<PageState, { headline: string; body: string }>
> = {
  error_not_found: {
    headline: "Invite not found",
    body: "This link doesn't match any pending invite. It may have already been used.",
  },
  error_expired: {
    headline: "Invite expired",
    body: "This invite link has expired. Ask your Kugie contact to send a new one.",
  },
  error_consumed: {
    headline: "Invite already used",
    body: "This invite link has already been redeemed. Try signing in instead.",
  },
};

function InviteSignUpPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>("loading");
  const [invite, setInvite] = useState<InviteInfo | null>(null);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [consent, setConsent] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    password?: string;
  }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load invite once on mount
  useEffect(() => {
    invites
      .preview(token)
      .then((data) => {
        if (new Date(data.expires_at) < new Date()) {
          setPageState("error_expired");
          return;
        }
        setInvite(data);
        setPageState("ready");
      })
      .catch((err: Error) => {
        if (err.message === "invite_not_found") setPageState("error_not_found");
        else if (err.message === "invite_expired")
          setPageState("error_expired");
        else if (err.message === "invite_consumed")
          setPageState("error_consumed");
        else setPageState("error_generic");
      });
  }, [token]);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const errors: typeof fieldErrors = {};
    if (name.trim().length < 2) errors.name = "Please enter your full name.";
    const pwErr = validatePassword(password);
    if (pwErr) errors.password = pwErr;
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setSubmitting(true);
    try {
      await invites.redeem({
        token,
        name: name.trim(),
        password,
        consent_at: new Date().toISOString(),
      });
      // Redeem issued a session cookie — hydrate the store before entering the
      // authed onboarding sub-tree so its guards see the new membership.
      await useAuthStore.getState().refresh();
      void navigate({ to: "/onboarding/channels" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "redeem_failed";
      if (msg === "invite_consumed") {
        setPageState("error_consumed");
      } else if (msg === "invite_expired") {
        setPageState("error_expired");
      } else if (msg === "email_taken") {
        setSubmitError(
          "An account with this email already exists. Try signing in instead.",
        );
      } else {
        setSubmitError(
          "Something went wrong. Please try again or contact support.",
        );
      }
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render: loading
  // ---------------------------------------------------------------------------

  if (pageState === "loading") {
    return (
      <PageShell>
        <div className="flex flex-col items-center gap-3 py-16 text-[color:var(--color-abyssal)]/50">
          <Spinner />
          <p className="text-sm">Checking your invite…</p>
        </div>
      </PageShell>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: error states
  // ---------------------------------------------------------------------------

  if (pageState !== "ready" || !invite) {
    const copy = ERROR_COPY[pageState] ?? {
      headline: "Something went wrong",
      body: "We couldn't load your invite. Please try again or contact support.",
    };

    return (
      <PageShell>
        <div className="rounded-2xl bg-white p-8 shadow-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--color-coral)]/10">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-[color:var(--color-coral)]"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="font-semibold text-lg text-[color:var(--color-abyssal)]">
            {copy.headline}
          </h2>
          <p className="mt-2 text-sm text-[color:var(--color-abyssal)]/60">
            {copy.body}
          </p>
        </div>
      </PageShell>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: form
  // ---------------------------------------------------------------------------

  return (
    <PageShell>
      <StepIndicator current={1} total={4} />

      <div className="mt-6">
        <p className="text-sm font-medium text-[color:var(--color-north-star)]">
          {invite.workspace_name}
        </p>
        <h1 className="mt-1 font-semibold tracking-tight text-3xl text-[color:var(--color-abyssal)]">
          Welcome to Jotty.
        </h1>
        <p className="mt-2 text-[color:var(--color-abyssal)]/60">
          Create your account to get started.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-7 space-y-4 rounded-2xl bg-white p-6 shadow-sm"
      >
        <Field label="Full name" error={fieldErrors.name}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Rina Kartika"
            autoComplete="name"
            autoFocus
            required
            className={inputCn(!!fieldErrors.name)}
          />
        </Field>

        {/* Work email — prefilled + read-only from invite token */}
        <Field
          label="Work email"
          hint="Pre-filled from your invite — cannot be changed."
        >
          <input
            type="email"
            value={invite.email}
            readOnly
            tabIndex={-1}
            className="w-full rounded-md border border-[color:var(--color-abyssal)]/10 bg-[color:var(--color-abyssal)]/[0.03] px-3 py-2 text-[color:var(--color-abyssal)]/50 select-none cursor-default outline-none"
          />
        </Field>

        <Field
          label="Password"
          error={fieldErrors.password}
          hint="At least 12 characters."
        >
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a password"
              autoComplete="new-password"
              required
              className={inputCn(!!fieldErrors.password) + " pr-10"}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-[color:var(--color-abyssal)]/40 hover:text-[color:var(--color-abyssal)]/70"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </Field>

        {/* Consent checkbox — required; timestamp persisted on user record */}
        <label className="flex items-start gap-3 cursor-pointer">
          <div className="relative mt-0.5 flex-shrink-0">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="peer sr-only"
              required
            />
            <div
              className={`h-4 w-4 rounded border transition-colors ${consent ? "border-[color:var(--color-north-star)] bg-[color:var(--color-north-star)]" : "border-[color:var(--color-abyssal)]/25 bg-white"}`}
            >
              {consent && (
                <svg
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  className="w-full h-full p-0.5"
                >
                  <path
                    d="M2 6l3 3 5-5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </div>
          <span className="text-sm text-[color:var(--color-abyssal)]/70 leading-snug">
            I’ll let meeting participants know that Jotty is recording.{" "}
            <a
              href="/privacy"
              className="text-[color:var(--color-north-star)] underline underline-offset-2"
            >
              Learn more
            </a>
          </span>
        </label>

        {submitError ? (
          <p className="text-sm text-[color:var(--color-coral)]">
            {submitError}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || !consent}
          className="mt-2 w-full rounded-md bg-[color:var(--color-north-star)] px-4 py-2.5 font-medium text-white transition-opacity disabled:opacity-40"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner small />
              Creating account…
            </span>
          ) : (
            "Create account"
          )}
        </button>
      </form>

      <p className="mt-5 text-center text-xs text-[color:var(--color-abyssal)]/40">
        By creating an account you agree to Jotty’s{" "}
        <a
          href="/terms"
          className="underline underline-offset-2 hover:text-[color:var(--color-abyssal)]/70"
        >
          Terms of Service
        </a>{" "}
        and{" "}
        <a
          href="/privacy"
          className="underline underline-offset-2 hover:text-[color:var(--color-abyssal)]/70"
        >
          Privacy Policy
        </a>
        .
      </p>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-16">
      <div className="mb-8 flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[color:var(--color-north-star)]">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M3 4h8M3 7h5M3 10h6"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <span className="font-semibold text-[color:var(--color-abyssal)]">
          Jotty
        </span>
      </div>
      {children}
    </main>
  );
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all ${
              i + 1 === current
                ? "w-6 bg-[color:var(--color-north-star)]"
                : i + 1 < current
                  ? "w-4 bg-[color:var(--color-north-star)]/40"
                  : "w-4 bg-[color:var(--color-abyssal)]/10"
            }`}
          />
        ))}
      </div>
      <span className="text-xs text-[color:var(--color-abyssal)]/50">
        Step {current} of {total}
      </span>
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-[color:var(--color-abyssal)]">
        {label}
      </span>
      {children}
      {error ? (
        <span className="mt-1 block text-xs text-[color:var(--color-coral)]">
          {error}
        </span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-[color:var(--color-abyssal)]/50">
          {hint}
        </span>
      ) : null}
    </label>
  );
}

function Spinner({ small }: { small?: boolean }) {
  const size = small ? 14 : 20;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="animate-spin"
      aria-hidden
    >
      <path
        d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"
        strokeLinecap="round"
      />
    </svg>
  );
}

function inputCn(hasError: boolean): string {
  return `w-full rounded-md border px-3 py-2 outline-none transition-colors ${
    hasError
      ? "border-[color:var(--color-coral)] focus:border-[color:var(--color-coral)]"
      : "border-[color:var(--color-abyssal)]/15 focus:border-[color:var(--color-north-star)]"
  } bg-white`;
}
